import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api-client'
import type { AxiosError } from 'axios'

export interface TimePeriod {
  id: number
  projectId: number
  periodType: 'week' | 'day'
  periodKey: string
  periodLabel: string
  startDate: string
  endDate: string
  sortOrder: number
}

export interface CurvaAggregate {
  periodKey: string
  periodLabel: string
  startDate: string
  endDate: string
  plannedSumWeight: number
  plannedCumulativeWeight: number
  actualSumWeight: number | null
  actualCumulativeWeight: number | null
  deviation: number | null
}

export interface CurvaSResponse {
  projectId: number
  projectName: string
  periodType: 'week' | 'day'
  totalPeriods: number
  rawData: any[]
  aggregates: CurvaAggregate[]
}

// ============================================
// Query Hooks
// ============================================

/**
 * Hook to fetch time periods for project
 */
export function useTimePeriods(projectId: number, periodType?: 'week' | 'day') {
  return useQuery<{ projectId: number; periods: TimePeriod[] }, AxiosError>({
    queryKey: ['time-periods', projectId, periodType],
    queryFn: async () => {
      const response = await apiClient.getTimePeriods(projectId, periodType)
      return response.data
    },
    enabled: !!projectId
  })
}

/**
 * Hook to fetch complete Curva S data with aggregates
 */
export function useCurvaS(projectId: number) {
  return useQuery<CurvaSResponse, AxiosError>({
    queryKey: ['curva-s', projectId],
    queryFn: async () => {
      const response = await apiClient.getCurvaSData(projectId)
      return response.data
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

// ============================================
// Mutation Hooks
// ============================================

/**
 * Hook to generate time periods
 */
export function useGenerateTimePeriods(projectId: number) {
  const queryClient = useQueryClient()

  return useMutation<
    any,
    AxiosError,
    { type: 'week' | 'day'; deleteExisting?: boolean }
  >({
    mutationFn: async (data) => {
      const response = await apiClient.generateTimePeriods(projectId, data.type, data.deleteExisting)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['time-periods', projectId]
      })
      queryClient.invalidateQueries({
        queryKey: ['curva-s', projectId]
      })
    },
    onError: (error) => {
      console.error('Failed to generate time periods:', error.response?.data)
    }
  })
}

/**
 * Hook to update planned weight
 */
export function useUpdatePlannedWeight(projectId: number) {
  const queryClient = useQueryClient()

  return useMutation<
    any,
    AxiosError,
    { itemId: number; periodId: number; plannedWeight: number }
  >({
    mutationFn: async ({ itemId, periodId, plannedWeight }) => {
      const response = await apiClient.updatePlannedWeight(itemId, periodId, plannedWeight)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['curva-s', projectId]
      })
    },
    onError: (error) => {
      console.error('Failed to update planned weight:', error.response?.data)
    }
  })
}

/**
 * Hook to update actual weight (realization)
 */
export function useUpdateActualWeight(projectId: number) {
  const queryClient = useQueryClient()

  return useMutation<
    any,
    AxiosError,
    { itemId: number; periodId: number; actualWeight: number }
  >({
    mutationFn: async ({ itemId, periodId, actualWeight }) => {
      const response = await apiClient.updateActualWeight(itemId, periodId, actualWeight)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['curva-s', projectId]
      })
    },
    onError: (error) => {
      console.error('Failed to update actual weight:', error.response?.data)
    }
  })
}

/**
 * Hook to export Curva S data as CSV
 */
export function useExportCurvaS(projectId: number) {
  return useMutation<any, AxiosError, void>({
    mutationFn: async () => {
      const response = await apiClient.exportCurvaSData(projectId)
      return response.data
    },
    onSuccess: (csvData) => {
      // Create download link
      const blob = new Blob([csvData], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `curva-s-export-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    },
    onError: (error) => {
      console.error('Failed to export Curva S data:', error.response?.data)
    }
  })
}

/**
 * Hook to import Curva S data from CSV
 */
export function useImportCurvaS(projectId: number) {
  const queryClient = useQueryClient()

  return useMutation<any, AxiosError, File>({
    mutationFn: async (file) => {
      const response = await apiClient.importCurvaSData(projectId, file)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['curva-s', projectId]
      })
    },
    onError: (error) => {
      console.error('Failed to import Curva S data:', error.response?.data)
    }
  })
}